<script setup lang="ts">
import { formatDate } from "@/helpers/date.helper";
import { isStartedSeriesBookable as getIsStartedSeriesBookable } from "@/helpers/activity.helper";
import { ActiviteitItem, ActiviteitLesWithTrainers } from "@/repositories/activiteit.repository";
import { ISportaanbieder, ISportaanbiederLocatie } from "@/repositories/sportaanbieder.repository";
import { computed, ref } from "vue";

const props = defineProps<{
    activiteit: ActiviteitItem | null;
    aanbieder: ISportaanbieder | null;
    locatie: ISportaanbiederLocatie | null;
    lessen: ActiviteitLesWithTrainers[] | null;
}>();

const detailsOpen = ref(false);

const emit = defineEmits<{
    (e: "open-lessons-overview"): void;
}>();

const hasLessen = computed(() => {
    return Array.isArray(props.lessen) && props.lessen.length > 0;
});

const eersteLes = computed(() => {
    if (!props.lessen?.length) {
        return null;
    }

    return [...props.lessen].sort((a, b) => {
        return new Date(a.BeginDatum).getTime() - new Date(b.BeginDatum).getTime();
    })[0];
});

const isStartedSeriesBookable = computed(() => {
    return getIsStartedSeriesBookable(
        props.activiteit?.InschrijvenBegonnenReeksMogelijk,
        props.lessen,
    );
});

const openLessonsOverview = () => {
    emit("open-lessons-overview");
};
</script>

<template>
    <div class="mobile-cart-box-info-container">
        <div class="payment-badge">
            <p>Jouw bestelling</p>
        </div>
        <h2 class="mobile-cart-box-info-title mb-1 h5">
            {{ activiteit?.Naam }}
        </h2>
        <p class="mobile-cart-box-info-title-provider">
            {{ aanbieder?.Naam }}
        </p>

        <div v-if="isStartedSeriesBookable" class="alert alert-warning font-size-small" role="status">
            Je schrijft je in voor een lesreeks die al begonnen is. In het lesoverzicht zie je welke lessen al zijn geweest.
        </div>

        <div class="payment-cart-mobile-chevron-box ml-0" @click="detailsOpen = !detailsOpen">
            <div class="payment-cart-mobile-chevron-circle">
                <span :class="detailsOpen ? 'icon-chevron--down' : 'icon-chevron--up'" aria-hidden="true"></span>
            </div>
            <p class="payment-cart-mobile-details">Toon details</p>
        </div>

        <div v-if="detailsOpen" class="mt-2">
            <div class="payment-cart__date-time d-flex align-items-start">
                <div class="payment-cart__date-time-icon d-block">
                    <span class="icon-clock"></span>
                </div>
                <div v-if="!hasLessen" class="payment-cart__date-time-info d-block font-size-sm">
                    <div class="payment-cart__date">
                        {{ formatDate(activiteit?.BeginDatum, { hour: '2-digit', minute: '2-digit' }) }} -
                        {{ formatDate(activiteit?.EindDatum, { hour: '2-digit', minute: '2-digit' }) }} <br>
                        {{ formatDate(activiteit?.BeginDatum, { weekday: 'long', day: 'numeric', month: 'long' }) }}
                    </div>
                    <div class="payment-cart__time"></div>
                </div>
                <div v-else class="payment-cart__date-time-info d-block font-size-sm">
                    <div class="payment-cart__time">
                        <p class="payment-cart__time-text">
                            vanaf {{ formatDate(eersteLes?.BeginDatum, {
                                weekday: 'long', day: 'numeric', month: 'long'
                            }) }}
                        </p>
                        <button class="payment-cart__button" type="button" @click="openLessonsOverview">
                            <span class="icon-plus"></span>
                            Toon lesoverzicht
                        </button>
                    </div>
                </div>
            </div>

            <div class="payment-cart__location d-flex align-items-start">
                <div class="payment-cart__location-icon d-block">
                    <span class="icon-map"></span>
                </div>
                <div class="payment-cart__location-info d-block font-size-sm">
                    <div class="payment-cart__location-address">
                        {{ locatie?.Adres }} <br>
                        <span v-if="locatie?.Postcode">{{ locatie?.Postcode }},&nbsp;</span>{{ locatie?.Woonplaats }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
